import { toEnglishUserMessage } from '../lib/messageI18n';

/**
 * API base: same-origin `/api/...` in production when PHP sits next to `dist/`.
 * Override with VITE_API_BASE e.g. https://example.com/m3u-studio (no trailing slash).
 */
export function apiUrl(path: string): string {
  const base = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

/**
 * URL do zasobu z backendu (np. `/picons/...` z search-logo.php).
 * - `VITE_API_BASE` — pełny prefix (subdir produkcji albo inny host).
 * - Inaczej ścieżka względna od aktualnego origin: w dev z proxy Vite (`/picons` → `VITE_DEV_PROXY_TARGET`) działa `<img src="/picons/...">`.
 * - Wartość z API bywa liczbą / innym typem — zawsze normalizuj przez `String`, inaczej `.trim()` rzuca i wywala cały React.
 */
export function publicAssetUrl(sitePath: unknown): string {
  if (sitePath == null || sitePath === '') return '';
  const s = String(sitePath).trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) {
    // Browsers block http images on https pages (mixed content),
    // so route insecure logos through backend proxy.
    if (
      /^http:\/\//i.test(s) &&
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:'
    ) {
      const base = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '');
      const proxyPath = `/api/media/logo_proxy.php?url=${encodeURIComponent(s)}`;
      return base ? `${base}${proxyPath}` : proxyPath;
    }
    return s;
  }
  const p = s.startsWith('/') ? s : `/${s}`;
  const base = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '');
  if (base) return `${base}${p}`;
  return p;
}

/**
 * Pełny URL do pokazania w polu tekstowym / skopiowania (np. `/picons/…` → `https://host/picons/…`).
 * Obrazki nadal używają `publicAssetUrl` (wystarczy ścieżka względna w `src`).
 */
export function displayPublicUrl(sitePath: unknown): string {
  const u = publicAssetUrl(sitePath);
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (/^\/\//.test(u)) {
    if (typeof window !== 'undefined' && window.location?.protocol) {
      return `${window.location.protocol}${u}`;
    }
    return `https:${u}`;
  }
  if (typeof window === 'undefined') return u;
  try {
    return new URL(u, window.location.origin).href;
  } catch {
    return u;
  }
}

export function getToken(): string | null {
  return localStorage.getItem('authToken');
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  return fetch(apiUrl(path), { ...init, headers });
}

function messageFromErrorBody(text: string): string | undefined {
  const t = text.trim();
  if (!t) return undefined;
  try {
    const j = JSON.parse(t) as { message?: string; error?: string };
    if (typeof j.message === 'string' && j.message.trim()) return j.message.trim();
    if (typeof j.error === 'string' && j.error.trim()) return j.error.trim();
    return undefined;
  } catch {
    return t.length > 200 ? `${t.slice(0, 200)}…` : t;
  }
}

/** Thrown when `apiJson` receives a non-OK HTTP status. */
export class ApiHttpError extends Error {
  readonly status: number;
  readonly path: string;
  readonly bodySnippet?: string;

  constructor(message: string, status: number, path: string, bodySnippet?: string) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.path = path;
    this.bodySnippet = bodySnippet;
  }
}

function isNetworkErrorMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized === 'failed to fetch' ||
    normalized.includes('networkerror') ||
    normalized.includes('network request failed') ||
    normalized.includes('load failed')
  );
}

/** User-facing message: prefers server text from {@link ApiHttpError}, then `Error.message`, else `fallback`. */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiHttpError) {
    if (error.status === 404 && /file not found/i.test(error.message)) {
      return (
        'Missing import API files on server (merge_import.php, merge_import_m3u.php, ...). ' +
        'Upload the api/playlists/ directory from repository.'
      );
    }
    return toEnglishUserMessage(error.message);
  }
  if (error instanceof Error) {
    return isNetworkErrorMessage(error.message)
      ? toEnglishUserMessage(fallback)
      : toEnglishUserMessage(error.message);
  }
  return toEnglishUserMessage(fallback);
}

/**
 * Parses JSON from a response body. On HTTP error, throws {@link ApiHttpError} with server message when possible.
 */
export async function apiJsonFromResponse<T>(path: string, res: Response): Promise<T> {
  const text = await res.text();
  if (!res.ok) {
    const hint = messageFromErrorBody(text);
    const msg = hint || `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`.trim();
    throw new ApiHttpError(msg, res.status, path, text ? text.slice(0, 500) : undefined);
  }
  const trimmed = text.trim();
  if (!trimmed) return {} as T;
  try {
    return JSON.parse(trimmed) as T;
  } catch {
    throw new Error(`Invalid JSON from ${path}: ${text.slice(0, 200)}`);
  }
}

/**
 * Parses JSON from a successful response. On HTTP error, throws {@link ApiHttpError} with server message when possible.
 */
export async function apiJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(path, init);
  return apiJsonFromResponse<T>(path, res);
}

/** Multipart POST — does not set Content-Type (browser sets boundary). */
export async function apiJsonFormData<T>(path: string, body: FormData): Promise<T> {
  const res = await apiFetch(path, { method: 'POST', body });
  return apiJsonFromResponse<T>(path, res);
}
